import React, { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Plus, X } from 'lucide-react';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { useLanguage } from '../../lib/i18n';
import { usePreferences } from './usePreferences';

interface NamesTermsSectionProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

/** Sınırlar ana süreçteki sanitize ile aynı (services/settings.ts). */
const MAX_TERMS = 100;
const MAX_TERM_LENGTH = 80;

/**
 * Ayarlar > İsimler ve terimler.
 *
 * Buraya yazılanlar iki yere gidiyor: Deepgram isteklerine `keyterm` olarak
 * (nova-3 bu adları öne alıyor) ve özet istemine "şöyle yazılmalı" notu
 * olarak. Bir sonraki kayıttan itibaren geçerli.
 */
export const NamesTermsSection: React.FC<NamesTermsSectionProps> = ({ onNotify }) => {
  const { t } = useLanguage();
  const { prefs, isLoaded, savePrefs } = usePreferences();
  const [terms, setTerms] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isLoaded) setTerms(prefs.transcriptTerms);
  }, [isLoaded, prefs.transcriptTerms]);

  // Kaydederken kutu kilitlenmiyor: kilitlenince odak gidiyor, telefonda
  // klavye her terimden sonra kapanıyordu.
  const persist = async (next: string[]) => {
    const previous = terms;
    // Otomatik kaydediliyor: önce ekranda göster, hata olursa geri al.
    setTerms(next);
    try {
      await savePrefs({ transcriptTerms: next });
    } catch (err) {
      setTerms(previous);
      onNotify('error', (err as Error).message || t('Terimler kaydedilemedi.', 'Could not save the terms.'));
    }
  };

  /** Virgülle ayrılmış bir liste yapıştırıldıysa hepsi tek seferde eklenir. */
  const addTerms = (raw: string) => {
    const candidates = raw
      .split(',')
      .map((term) => term.replace(/\s+/g, ' ').trim().slice(0, MAX_TERM_LENGTH))
      .filter(Boolean);
    if (candidates.length === 0) return;

    const next = [...terms];
    for (const candidate of candidates) {
      const exists = next.some((term) => term.toLocaleLowerCase('tr') === candidate.toLocaleLowerCase('tr'));
      if (!exists && next.length < MAX_TERMS) next.push(candidate);
    }
    if (next.length !== terms.length) void persist(next);
  };

  const addFromDraft = () => {
    addTerms(draft);
    setDraft('');
  };

  /**
   * Virgül yazılınca öncesi hemen terim olur — telefon klavyesinde Enter
   * her zaman yok. Boşlukta bölmüyoruz: "Kadir Çetin" tek terim kalmalı.
   */
  const onDraftChange = (value: string) => {
    const cut = value.lastIndexOf(',');
    if (cut === -1) {
      setDraft(value);
      return;
    }
    addTerms(value.slice(0, cut));
    setDraft(value.slice(cut + 1).trimStart());
  };

  const remove = (term: string) => {
    void persist(terms.filter((item) => item !== term));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addFromDraft();
      return;
    }
    // Boş kutuda geri silme son terimi çıkarır.
    if (event.key === 'Backspace' && draft === '' && terms.length > 0) {
      event.preventDefault();
      remove(terms[terms.length - 1]);
    }
  };

  const isFull = terms.length >= MAX_TERMS;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={draft}
              disabled={isFull}
              maxLength={MAX_TERM_LENGTH * 4}
              enterKeyHint="done"
              placeholder={isFull ? t('Liste dolu', 'The list is full') : t('ör. Kadir Çetin', 'e.g. Priya Raghunathan')}
              onChange={(event) => onDraftChange(event.target.value)}
              onKeyDown={onKeyDown}
              onBlur={addFromDraft}
              aria-label={t('İsim ya da terim ekle', 'Add a name or term')}
            />
            <Button
              variant="outline"
              disabled={isFull || draft.trim() === ''}
              // Odak kutuda kalsın: telefonda klavye kapanmasın, arka arkaya eklenebilsin.
              onPointerDown={(event) => event.preventDefault()}
              onClick={() => {
                addFromDraft();
                inputRef.current?.focus();
              }}
            >
              <Plus className="size-4" />
              {t('Ekle', 'Add')}
            </Button>
          </div>

          {terms.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {terms.map((term) => (
                <li
                  key={term}
                  className="flex items-center gap-1.5 rounded-full border border-border bg-field py-1 pl-3 pr-1.5 text-sm"
                >
                  <span className="max-w-[18rem] truncate">{term}</span>
                  <button
                    type="button"
                    onClick={() => remove(term)}
                    aria-label={t(`"${term}" terimini kaldır`, `Remove "${term}"`)}
                    className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-between gap-4 text-[0.8rem] text-muted-foreground">
            <span>
              {t(
                'Her birinden sonra virgül koy ya da Ekle’ye bas. Otomatik kaydedilir.',
                'Type a comma after each one or tap Add. Saves automatically.'
              )}
            </span>
            <span className="tabular-nums">
              {terms.length} / {MAX_TERMS}
            </span>
          </div>
        </CardContent>
      </Card>

      <p className="text-[0.8rem] leading-relaxed text-muted-foreground">
        {t(
          'Bir ekip arkadaşının tam adı, bir müşteri, bir ürün, bir kısaltma — transkriptin sürekli yanlış yazdığı her şey. Birkaçını birden eklemek için virgülle ayrılmış bir liste yapıştırabilirsin.',
          'A teammate’s full name, a client, a product, an acronym — anything a transcript keeps getting wrong. Paste a comma-separated list to add several at once.'
        )}
      </p>
    </div>
  );
};
